import { Dropdown, Input, Spin, Tooltip, message } from 'antd';
import { PlusOutlined, SearchOutlined, SettingOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import CreateCustomLensModal from 'components/molecules/PPA/Modals/CustomLense/CreateCustomLensModal';
import EditSystemLensModal from 'components/molecules/PPA/Modals/CustomLense/EditSystemLensModal';
import { getCustomLensLimitFromApi, getLensCanEditFromApi, getLensCanModifyCatalogFromApi } from 'components/molecules/PPA/Modals/CustomLense/customLens.helpers';
import { getCustomLensLimitReachedMessage } from 'components/molecules/PPA/Modals/CustomLense/customLens.constants';
import { CUSTOM_LENS_TOOLTIP_PROPS } from 'components/molecules/PPA/Modals/CustomLense/customLens.ui';
import type { CustomLensFormData, UpsertCustomLensResponse } from 'components/molecules/PPA/Modals/CustomLense/customLens.types';
import useFetchViewMetadata, { fetchScenarioViewMetadata, getPpaViewMetadataQueryKey } from 'hooks/ppa/useFetchViewMetadata';
import useFetchLensConfig, { fetchLensConfig } from 'hooks/ppa/useFetchLensConfig';
import useFetchLensScenarioConfig, { fetchLensScenarioConfig } from 'hooks/ppa/useFetchLensScenarioConfig';
import { savePpaToView, saveScenarioDeviation, setScenarioActiveLens, fetchPpaViewMetadata } from 'hooks/ppa/usePpaViews';
import { invalidateLensDeleteWorkspaceCache } from 'hooks/ppa/ppaViewPrefetch.helpers';
import type { PPAPendingUserMessage } from 'types/ppaViews';
import { getAuthUserMail } from 'utils/helperMethods';

import ScenarioLensTableShell from 'components/molecules/PricingSimulatorV3/ScenarioLensTable/ScenarioLensTableShell';
import SimulatorContextBanner from 'components/molecules/PricingSimulatorV3/SimulatorContextBanner/SimulatorContextBanner';
import usePendingContextMessages from 'components/molecules/PricingSimulatorV3/SimulatorContextBanner/usePendingContextMessages';
import LensSettingsSortableList from './LensSettingsSortableList';
import LensConfigurationPanel from './LensConfigurationPanel';
import {
    cloneLensCatalog,
    hasCatalogLensSelection,
    materializeScenarioLensCatalogIfEmpty,
    validateLensCatalogSelection,
} from './scenarioLensCatalog.helpers';
import type { LensCatalogData, LensCatalogFetchResult } from './scenarioLensCatalog.types';
import {
    clearScenarioLensConfigCache,
    getCachedLensConfigResult,
    getLensScenarioConfigQueryKey,
    hasCachedLensCatalog,
    resolveLensCatalog,
} from './scenarioLensConfigCache.helpers';
import {
    appendCustomLens,
    applyLensDraftToView,
    cloneScenarioView,
    countPrivateLenses,
    deriveLensIsSelected,
    getActiveLens,
    getVisibleLenses,
    hasSettingsPanelDraftChanges,
    mergeViewLensesIntoDraft,
    normalizeLensDisplayOrder,
    normalizeScenarioView,
    removeCustomLensFromView,
    renameCustomLensInView,
    reorderLensOnCatalogSelectionChange,
    reorderLensesSelectedFirst,
    resolveActiveLensIdAfterDelete,
    resolveLensIdAfterDelete,
    sortLensesByDisplayOrder,
    diagnoseViewDirtyState,
    VIEW_DIRTY_REASON_LABELS,
    syncViewLensSelectionFromCatalogs,
} from './scenarioLens.helpers';
import {
    buildColumnVisibilityFromLenses,
    buildFullScenarioViewPayload,
    computeEditedCatalogLensIds,
} from './viewSavePayload.helpers';
import type {
    LiveViewConfig,
    ScenarioLensSectionController,
    ScenarioLensSectionProps,
    ScenarioViewData,
    ScenarioViewLens,
} from './scenarioLens.types';
import {
    LENS_SETTINGS_DROPDOWN_CLASS,
    StyledCustomLensRow,
    StyledLensContent,
    StyledLensSettingsDropdownGlobalStyles,
    StyledLensSettingsDropdownWrapper,
    StyledLensConfigurationDropdown,
    StyledLensConfigurationLoading,
    StyledLensSettingsPanel,
    StyledLensSettingsSearch,
    StyledLensTab,
    StyledLensTabsInner,
    StyledLensTabsRow,
    StyledLensTabsTrack,
    StyledSettingsButton,
} from './ScenarioLensSection.styles';

const ScenarioLensSection = ({ scenarioName, scenarioId = null, viewId = null, dataSource = 'view', isViewDetached = false, viewListReady = true, isOwner, scenarioOwner, onDirtyChange, onContentLoadingChange, lensControllerRef }: ScenarioLensSectionProps) => {
    const canPersistScenarioView = isOwner === true;
    const {
        data: fetchedViewData,
        isLoading: isViewLoading,
        isPending: isViewPending,
    } = useFetchViewMetadata({
        viewId,
        scenarioId,
        dataSource,
        enabled: viewListReady && viewId != null,
    });

    const [expandedLensId, setExpandedLensId] = useState<number | null>(null);
    const [catalogConfigRevision, setCatalogConfigRevision] = useState(0);
    const [isRefreshingExpandedLensConfig, setIsRefreshingExpandedLensConfig] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [view, setView] = useState<ScenarioViewData | null>(null);
    const [draftLenses, setDraftLenses] = useState<ScenarioViewLens[]>([]);
    const [settingsSearch, setSettingsSearch] = useState('');
    const [isCustomLensModalOpen, setIsCustomLensModalOpen] = useState(false);
    const [isEditCustomLensModalOpen, setIsEditCustomLensModalOpen] = useState(false);
    const [isEditSystemLensModalOpen, setIsEditSystemLensModalOpen] = useState(false);
    const [editingLens, setEditingLens] = useState<ScenarioViewLens | null>(null);
    const [catalogDraftsByLensId, setCatalogDraftsByLensId] = useState<Record<number, LensCatalogData>>({});
    const [catalogEditingByLensId, setCatalogEditingByLensId] = useState<Record<number, LensCatalogData>>({});
    const catalogDraftsByLensIdRef = useRef(catalogDraftsByLensId);
    const catalogEditingByLensIdRef = useRef(catalogEditingByLensId);
    const [isDirty, setIsDirty] = useState(false);
    const baselineLensesRef = useRef<ScenarioViewLens[]>([]);
    const hydratedViewContextRef = useRef<string | null>(null);
    const isDirtyRef = useRef(false);
    const hasLocalViewEditsRef = useRef(false);
    const viewRef = useRef<ScenarioViewData | null>(null);
    const draftLensesRef = useRef<ScenarioViewLens[]>([]);
    const lensIdsNeedingFreshConfigAfterEditRef = useRef<Set<number>>(new Set());
    /** Catalog checkbox state when the right config panel was opened for each lens. */
    const expandedLensPanelBaselineRef = useRef<Record<number, LensCatalogData>>({});
    /** Base view `updated_at` when the workspace view was loaded — required for save_to_view. */
    const baseViewUpdatedAtRef = useRef<string | null>(null);
    /** Monotonic counter so stale active-lens persist responses are ignored. */
    const activeLensPersistSeqRef = useRef(0);
    /** Skip settings close commit/reset when opening a lens edit modal (preserves table catalog drafts). */
    const skipSettingsCloseSideEffectsRef = useRef(false);
    const queryClient = useQueryClient();

    const getViewContextKey = useCallback(
        () => `${scenarioId ?? 'none'}:${viewId ?? 'none'}:${dataSource}`,
        [dataSource, scenarioId, viewId],
    );

    viewRef.current = view;
    draftLensesRef.current = draftLenses;
    catalogDraftsByLensIdRef.current = catalogDraftsByLensId;
    catalogEditingByLensIdRef.current = catalogEditingByLensId;

    const activeLens = useMemo(() => (view ? getActiveLens(view) : null), [view]);
    const activeLensId = activeLens?.lens_id ?? null;
    const resolvedScenarioId = scenarioId ?? null;
    const isExpandedLensSameAsActive = expandedLensId != null && expandedLensId === activeLensId;

    const {
        data: activeLensConfigResult,
        isLoading: isActiveLensConfigLoading,
        isPending: isActiveLensConfigPending,
        isFetching: isActiveLensConfigFetching,
    } = useFetchLensScenarioConfig(activeLensId, resolvedScenarioId, activeLensId != null && resolvedScenarioId != null);

    const lensPendingMessages = useMemo(() => {
        const raw = activeLensConfigResult?.raw as { pending_messages?: PPAPendingUserMessage[] } | undefined;
        return raw?.pending_messages ?? [];
    }, [activeLensConfigResult]);

    const { messages: contextMessages, dismiss: dismissContextMessage } = usePendingContextMessages({
        viewMessages: fetchedViewData?.pending_messages ?? [],
        lensMessages: lensPendingMessages,
    });

    const shouldFetchExpandedLensConfig =
        isSettingsOpen &&
        expandedLensId != null &&
        resolvedScenarioId != null &&
        !isExpandedLensSameAsActive &&
        !hasCachedLensCatalog(expandedLensId, resolvedScenarioId, queryClient);

    const {
        data: expandedLensConfigResult,
        isLoading: isExpandedLensQueryLoading,
        isPending: isExpandedLensQueryPending,
        isFetching: isExpandedLensQueryFetching,
    } = useFetchLensScenarioConfig(expandedLensId, resolvedScenarioId, shouldFetchExpandedLensConfig);

    const { data: createLensConfigResult } = useFetchLensConfig(null, isSettingsOpen);

    const ensureLensConfigLoaded = useCallback(
        async (lensId: number) => {
            if (resolvedScenarioId == null) {
                return;
            }

            if (hasCachedLensCatalog(lensId, resolvedScenarioId, queryClient)) {
                return;
            }

            await queryClient.fetchQuery({
                queryKey: getLensScenarioConfigQueryKey(lensId, resolvedScenarioId),
                queryFn: () => fetchLensScenarioConfig(lensId, resolvedScenarioId),
            });
        },
        [queryClient, resolvedScenarioId],
    );

    const clearLensConfigCache = useCallback(
        (lensId: number) => {
            queryClient.removeQueries({ queryKey: ['ppa-lens-config', lensId] });
            if (resolvedScenarioId != null) {
                queryClient.removeQueries({
                    queryKey: getLensScenarioConfigQueryKey(lensId, resolvedScenarioId),
                });
            }
        },
        [queryClient, resolvedScenarioId],
    );

    const markLensConfigStaleAfterEdit = useCallback(
        (lensId: number) => {
            clearLensConfigCache(lensId);
            lensIdsNeedingFreshConfigAfterEditRef.current.add(lensId);
        },
        [clearLensConfigCache],
    );

    const refetchLensConfigAfterEdit = useCallback(
        async (lensId: number) => {
            if (resolvedScenarioId == null) {
                return;
            }

            setIsRefreshingExpandedLensConfig(true);
            try {
                await Promise.all([
                    queryClient.fetchQuery({
                        queryKey: getLensScenarioConfigQueryKey(lensId, resolvedScenarioId),
                        queryFn: () => fetchLensScenarioConfig(lensId, resolvedScenarioId),
                        staleTime: 0,
                    }),
                    queryClient.fetchQuery({
                        queryKey: ['ppa-lens-config', lensId],
                        queryFn: () => fetchLensConfig(lensId),
                        staleTime: 0,
                    }),
                ]);
                setCatalogConfigRevision((revision) => revision + 1);
            } finally {
                setIsRefreshingExpandedLensConfig(false);
            }
        },
        [queryClient, resolvedScenarioId],
    );

    const loadExpandedLensConfig = useCallback(
        async (lensId: number) => {
            if (lensIdsNeedingFreshConfigAfterEditRef.current.has(lensId)) {
                lensIdsNeedingFreshConfigAfterEditRef.current.delete(lensId);
                await refetchLensConfigAfterEdit(lensId);
                return;
            }

            await ensureLensConfigLoaded(lensId);
        },
        [ensureLensConfigLoaded, refetchLensConfigAfterEdit],
    );

    useEffect(() => {
        if (viewId == null) {
            if (isViewDetached) {
                baseViewUpdatedAtRef.current = null;
                return;
            }

            hydratedViewContextRef.current = null;
            baseViewUpdatedAtRef.current = null;
            setView(null);
            setDraftLenses([]);
            baselineLensesRef.current = [];
            setCatalogDraftsByLensId({});
            setCatalogEditingByLensId({});
            setExpandedLensId(null);
            setIsSettingsOpen(false);
            lensIdsNeedingFreshConfigAfterEditRef.current.clear();
            hasLocalViewEditsRef.current = false;
            isDirtyRef.current = false;
            setIsDirty(false);
            return;
        }

        if (!fetchedViewData || isViewLoading || isViewPending) {
            setView(null);
            return;
        }

        const viewContextKey = getViewContextKey();
        if (hydratedViewContextRef.current === viewContextKey && (isDirtyRef.current || hasLocalViewEditsRef.current)) {
            const localView = viewRef.current;
            if (localView) {
                setDraftLenses((currentDraftLenses) => mergeViewLensesIntoDraft(localView, currentDraftLenses));
            }
            return;
        }

        hydratedViewContextRef.current = viewContextKey;
        baseViewUpdatedAtRef.current = fetchedViewData.updated_at ?? null;
        clearScenarioLensConfigCache(resolvedScenarioId, queryClient);
        const nextView = normalizeScenarioView(cloneScenarioView(fetchedViewData));
        setView(nextView);
        const orderedLenses = sortLensesByDisplayOrder(nextView.lenses);
        setDraftLenses(orderedLenses);
        baselineLensesRef.current = orderedLenses;
        setCatalogDraftsByLensId({});
        setCatalogEditingByLensId({});
        hasLocalViewEditsRef.current = false;
        isDirtyRef.current = false;
        setIsDirty(false);
    }, [dataSource, fetchedViewData, getViewContextKey, isViewDetached, isViewLoading, isViewPending, queryClient, resolvedScenarioId, scenarioId, viewId]);

    useEffect(() => {
        isDirtyRef.current = isDirty;
    }, [isDirty]);

    const isViewDataLoading = viewId != null && (isViewLoading || isViewPending || !view);
    const isContentLoading =
        isViewDetached && view != null ? false : viewId == null || isViewDataLoading;

    useEffect(() => {
        onContentLoadingChange?.(isContentLoading);
    }, [isContentLoading, onContentLoadingChange]);

    useEffect(() => {
        if (!isDirty || !canPersistScenarioView) return;
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [canPersistScenarioView, isDirty]);

    useEffect(() => {
        if (!canPersistScenarioView) {
            onDirtyChange?.(false);
            return;
        }

        onDirtyChange?.(isDirty);
    }, [canPersistScenarioView, isDirty, onDirtyChange]);

    const resolveLensTabIsSelected = useCallback(
        (lensId: number): boolean | null => {
            const draft = catalogDraftsByLensId[lensId];
            if (draft) {
                return deriveLensIsSelected(draft);
            }

            const cachedCatalog = resolveLensCatalog(
                lensId,
                resolvedScenarioId,
                catalogDraftsByLensId,
                queryClient,
                lensId === activeLensId ? activeLensConfigResult : null,
            );
            if (cachedCatalog) {
                return deriveLensIsSelected(cachedCatalog);
            }

            return null;
        },
        [
            activeLensConfigResult,
            activeLensId,
            catalogDraftsByLensId,
            queryClient,
            resolvedScenarioId,
        ],
    );

    const resolveBaselineCatalog = useCallback(
        (lensId: number): LensCatalogData | null =>
            resolveLensCatalog(
                lensId,
                resolvedScenarioId,
                {},
                queryClient,
                lensId === activeLensId ? activeLensConfigResult : null,
            ),
        [activeLensConfigResult, activeLensId, queryClient, resolvedScenarioId],
    );

    const resolveCatalogForLens = useCallback(
        (lensId: number): LensCatalogData | null => {
            const inFlightEdit = catalogEditingByLensId[lensId];
            if (inFlightEdit) {
                return inFlightEdit;
            }

            const draft = catalogDraftsByLensId[lensId];
            if (draft) {
                return draft;
            }

            return resolveBaselineCatalog(lensId);
        },
        [catalogDraftsByLensId, catalogEditingByLensId, resolveBaselineCatalog],
    );

    const logViewDirtyDiagnostics = useCallback(
        (
            source: string,
            diagnostics: ReturnType<typeof diagnoseViewDirtyState>,
            previousDirty: boolean,
        ) => {
            const { isDirty, reasons, details } = diagnostics;
            const transitioned =
                previousDirty === isDirty
                    ? 'unchanged'
                    : isDirty
                      ? 'clean→dirty'
                      : 'dirty→clean';

            if (!isDirty && transitioned === 'unchanged') {
                return;
            }

            console.info('[PPA View Dirty]', {
                source,
                isDirty,
                transitioned,
                reasons: reasons.map((reason) => ({
                    code: reason,
                    label: VIEW_DIRTY_REASON_LABELS[reason],
                })),
                details,
                viewId,
                scenarioId,
                dataSource,
            });
        },
        [dataSource, scenarioId, viewId],
    );

    const syncDirtyFromBaseline = useCallback(
        (
            nextView?: ScenarioViewData,
            nextDraftLenses?: ScenarioViewLens[],
            source = 'sync_dirty_from_baseline',
        ) => {
            if (!canPersistScenarioView) {
                return;
            }

            const currentView = nextView ?? viewRef.current;
            if (!currentView) {
                return;
            }

            const previousDirty = isDirtyRef.current;
            const diagnostics = diagnoseViewDirtyState({
                baselineLenses: baselineLensesRef.current,
                viewLenses: currentView.lenses,
                draftLenses: nextDraftLenses ?? draftLensesRef.current,
                catalogDraftsByLensId,
                resolveBaselineCatalog,
            });
            const hasEdits = diagnostics.isDirty;

            logViewDirtyDiagnostics(source, diagnostics, previousDirty);

            hasLocalViewEditsRef.current = hasEdits;
            isDirtyRef.current = hasEdits;
            setIsDirty(hasEdits);
        },
        [
            canPersistScenarioView,
            catalogDraftsByLensId,
            logViewDirtyDiagnostics,
            resolveBaselineCatalog,
        ],
    );

    const buildFullSavePayload = useCallback((): ReturnType<typeof buildFullScenarioViewPayload> | null => {
        if (!view || !scenarioId || !canPersistScenarioView) {
            return null;
        }

        const syncedView = syncViewLensSelectionFromCatalogs(view, resolveLensTabIsSelected);
        return buildFullScenarioViewPayload({
            scenarioId,
            userId: getAuthUserMail() || undefined,
            syncedLenses: syncedView.lenses,
            resolveCatalogForLens,
            fallbackColumnVisibility: view.column_visibility,
        });
    }, [canPersistScenarioView, resolveCatalogForLens, resolveLensTabIsSelected, scenarioId, view]);

    const getLiveViewConfig = useCallback((): LiveViewConfig | null => {
        if (!view) {
            return null;
        }

        const syncedView = syncViewLensSelectionFromCatalogs(view, resolveLensTabIsSelected);
        const orderedLenses = sortLensesByDisplayOrder(syncedView.lenses);
        const activeLens = getActiveLens(syncedView);

        return {
            lens_config: orderedLenses.map((lens) => ({
                lens_id: lens.lens_id,
                is_selected: lens.is_selected,
                display_order: lens.display_order,
                is_default_lens: lens.is_default_lens ?? false,
            })),
            group_by: [...view.group_by]
                .sort((left, right) => left.display_order - right.display_order)
                .map((item) => ({
                    attribute_key: item.attribute_key,
                    display_order: item.display_order,
                })),
            active_lens_id: activeLens?.lens_id ?? null,
            column_visibility: buildColumnVisibilityFromLenses({
                lenses: orderedLenses,
                resolveCatalogForLens,
                fallbackColumnVisibility: view.column_visibility,
            }),
        };
    }, [resolveCatalogForLens, resolveLensTabIsSelected, view]);

    const commitPersistedState = useCallback(
        (syncedView: ScenarioViewData) => {
            const orderedSyncedLenses = sortLensesByDisplayOrder(syncedView.lenses);
            setView(syncedView);
            setDraftLenses(orderedSyncedLenses);
            baselineLensesRef.current = orderedSyncedLenses;
            setCatalogDraftsByLensId({});
            setCatalogEditingByLensId({});
            hasLocalViewEditsRef.current = false;
            isDirtyRef.current = false;
            setIsDirty(false);
        },
        [],
    );

    const saveScenarioView = useCallback(async () => {
        if (!view || !scenarioId || !canPersistScenarioView) {
            return;
        }

        const payload = buildFullSavePayload();
        if (!payload) {
            return;
        }

        try {
            await saveScenarioDeviation(payload);

            const syncedView = syncViewLensSelectionFromCatalogs(view, resolveLensTabIsSelected);
            const editedLensIds = [
                ...new Set([
                    ...computeEditedCatalogLensIds({
                        catalogDraftsByLensId,
                        catalogEditingByLensId,
                        resolveBaselineCatalog,
                    }),
                    ...lensIdsNeedingFreshConfigAfterEditRef.current,
                ]),
            ];

            if (editedLensIds.length > 0) {
                await Promise.all(
                    editedLensIds.map((lensId) =>
                        queryClient.fetchQuery({
                            queryKey: getLensScenarioConfigQueryKey(lensId, scenarioId),
                            queryFn: () => fetchLensScenarioConfig(lensId, scenarioId),
                            staleTime: 0,
                        }),
                    ),
                );

                for (const lensId of editedLensIds) {
                    lensIdsNeedingFreshConfigAfterEditRef.current.delete(lensId);
                }
            }

            commitPersistedState({
                ...syncedView,
                column_visibility: payload.column_visibility ?? syncedView.column_visibility,
            });
        } catch {
            void message.error('Failed to save scenario view');
            throw new Error('Failed to save scenario view');
        }
    }, [
        buildFullSavePayload,
        canPersistScenarioView,
        catalogDraftsByLensId,
        catalogEditingByLensId,
        commitPersistedState,
        queryClient,
        resolveBaselineCatalog,
        resolveLensTabIsSelected,
        scenarioId,
        view,
    ]);

    const saveScenarioAndUpdateView = useCallback(async () => {
        await saveScenarioView();
        if (!scenarioId) {
            return;
        }

        let baseViewUpdatedAt = baseViewUpdatedAtRef.current ?? viewRef.current?.updated_at ?? null;
        if (!baseViewUpdatedAt && viewId != null) {
            const metadata = await fetchPpaViewMetadata(viewId, {
                scenarioId,
                dataSource: 'view',
            });
            baseViewUpdatedAt = metadata.updated_at;
        }

        if (!baseViewUpdatedAt) {
            void message.error('Unable to save view. Please reload the view and try again.');
            throw new Error('base_view_updated_at is required when saving deviations to view');
        }

        await savePpaToView(scenarioId, { baseViewUpdatedAt });
    }, [saveScenarioView, scenarioId, viewId]);

    const resetToPersisted = useCallback(async () => {
        const modifiedLensIds = Object.keys(catalogDraftsByLensId).map(Number);

        setCatalogDraftsByLensId({});
        setCatalogEditingByLensId({});
        setExpandedLensId(null);
        setIsSettingsOpen(false);
        setSettingsSearch('');
        expandedLensPanelBaselineRef.current = {};
        lensIdsNeedingFreshConfigAfterEditRef.current.clear();
        hasLocalViewEditsRef.current = false;
        isDirtyRef.current = false;
        setIsDirty(false);

        if (viewId == null) {
            return;
        }

        if (resolvedScenarioId != null) {
            for (const lensId of modifiedLensIds) {
                queryClient.removeQueries({
                    queryKey: getLensScenarioConfigQueryKey(lensId, resolvedScenarioId),
                });
            }
        }

        const metadataQueryKey = getPpaViewMetadataQueryKey(viewId, resolvedScenarioId, dataSource);
        const cachedMetadata = queryClient.getQueryData<ScenarioViewData>(metadataQueryKey);

        if (cachedMetadata) {
            const nextView = normalizeScenarioView(cloneScenarioView(cachedMetadata));
            setView(nextView);
            const orderedLenses = sortLensesByDisplayOrder(nextView.lenses);
            setDraftLenses(orderedLenses);
            baselineLensesRef.current = orderedLenses;
            hydratedViewContextRef.current = getViewContextKey();
            return;
        }

        hydratedViewContextRef.current = null;
        await queryClient.fetchQuery({
            queryKey: metadataQueryKey,
            queryFn: () =>
                fetchScenarioViewMetadata(viewId, {
                    scenarioId: resolvedScenarioId,
                    dataSource,
                }),
        });
    }, [
        catalogDraftsByLensId,
        dataSource,
        getViewContextKey,
        queryClient,
        resolvedScenarioId,
        viewId,
    ]);

    useEffect(() => {
        if (!lensControllerRef) {
            return;
        }

        if (!canPersistScenarioView) {
            lensControllerRef.current = null;
            return;
        }

        const controller: ScenarioLensSectionController = {
            isDirty: () => isDirty,
            buildFullSavePayload,
            getLiveViewConfig,
            saveScenarioView,
            saveScenarioAndUpdateView,
            resetToPersisted,
        };
        lensControllerRef.current = controller;

        return () => {
            lensControllerRef.current = null;
        };
    }, [
        buildFullSavePayload,
        canPersistScenarioView,
        getLiveViewConfig,
        isDirty,
        lensControllerRef,
        resetToPersisted,
        saveScenarioAndUpdateView,
        saveScenarioView,
    ]);

    const expandedLensQueryResult = isExpandedLensSameAsActive ? activeLensConfigResult : expandedLensConfigResult;

    const expandedCommittedCatalog = useMemo(
        () =>
            expandedLensId == null
                ? null
                : resolveLensCatalog(
                      expandedLensId,
                      resolvedScenarioId,
                      catalogDraftsByLensId,
                      queryClient,
                      expandedLensQueryResult,
                  ),
        [catalogDraftsByLensId, expandedLensId, expandedLensQueryResult, queryClient, resolvedScenarioId],
    );

    const expandedPanelCatalog = useMemo(() => {
        if (expandedLensId == null) {
            return null;
        }

        return (
            catalogEditingByLensId[expandedLensId] ??
            catalogDraftsByLensId[expandedLensId] ??
            expandedCommittedCatalog
        );
    }, [
        catalogDraftsByLensId,
        catalogEditingByLensId,
        expandedCommittedCatalog,
        expandedLensId,
    ]);

    const resolvedActiveCatalog = useMemo(
        () =>
            resolveLensCatalog(
                activeLensId,
                resolvedScenarioId,
                catalogDraftsByLensId,
                queryClient,
                activeLensConfigResult,
            ),
        [activeLensId, activeLensConfigResult, catalogDraftsByLensId, queryClient, resolvedScenarioId],
    );

    const isActiveLensCatalogLoading =
        activeLensId != null &&
        !resolvedActiveCatalog &&
        (isActiveLensConfigLoading || isActiveLensConfigPending || isActiveLensConfigFetching);

    const isExpandedLensQueryInFlight = isExpandedLensSameAsActive
        ? isActiveLensConfigLoading || isActiveLensConfigPending || isActiveLensConfigFetching
        : isExpandedLensQueryLoading || isExpandedLensQueryPending || isExpandedLensQueryFetching;

    const clearExpandedLensPanelBaseline = useCallback((lensId: number) => {
        if (!expandedLensPanelBaselineRef.current[lensId]) {
            return;
        }

        const next = { ...expandedLensPanelBaselineRef.current };
        delete next[lensId];
        expandedLensPanelBaselineRef.current = next;
    }, []);

    const discardCatalogEditingForLens = useCallback((lensId: number) => {
        setCatalogEditingByLensId((current) => {
            if (!current[lensId]) {
                return current;
            }

            const next = { ...current };
            delete next[lensId];
            return next;
        });
    }, []);

    useEffect(() => {
        if (expandedLensId == null || !expandedCommittedCatalog || catalogEditingByLensId[expandedLensId]) {
            return;
        }

        expandedLensPanelBaselineRef.current[expandedLensId] = cloneLensCatalog(expandedCommittedCatalog);
    }, [catalogEditingByLensId, expandedCommittedCatalog, expandedLensId]);

    const handleExpandLens = useCallback(
        (lensId: number) => {
            setExpandedLensId((currentLensId) => {
                const nextLensId = currentLensId === lensId ? null : lensId;

                if (currentLensId != null && currentLensId !== nextLensId) {
                    discardCatalogEditingForLens(currentLensId);
                    clearExpandedLensPanelBaseline(currentLensId);
                }

                if (nextLensId == null && currentLensId != null) {
                    clearExpandedLensPanelBaseline(currentLensId);
                }

                if (nextLensId != null) {
                    clearExpandedLensPanelBaseline(nextLensId);
                    void loadExpandedLensConfig(nextLensId);
                }

                return nextLensId;
            });
        },
        [clearExpandedLensPanelBaseline, discardCatalogEditingForLens, loadExpandedLensConfig],
    );

    const resolveSettingsLensIsSelected = useCallback(
        (lensId: number, fallbackSelected: boolean): boolean => {
            const catalogDraft = catalogDraftsByLensId[lensId];
            if (catalogDraft) {
                return hasCatalogLensSelection(catalogDraft);
            }

            return fallbackSelected;
        },
        [catalogDraftsByLensId],
    );

    const settingsListLenses = useMemo(() => {
        const sourceLenses = view ? mergeViewLensesIntoDraft(view, draftLenses) : draftLenses;

        return reorderLensesSelectedFirst(
            sourceLenses.map((lens) => ({
                ...lens,
                is_selected: resolveSettingsLensIsSelected(lens.lens_id, lens.is_selected),
            })),
        );
    }, [draftLenses, resolveSettingsLensIsSelected, view]);

    useEffect(() => {
        if (expandedLensId == null || !catalogDraftsByLensId[expandedLensId]) {
            return;
        }

        const expandedCatalogDraft = catalogDraftsByLensId[expandedLensId];
        const hasSelection = hasCatalogLensSelection(expandedCatalogDraft);
        setDraftLenses((currentDraftLenses) => {
            const lens = currentDraftLenses.find((item) => item.lens_id === expandedLensId);
            if (!lens || lens.is_selected === hasSelection) {
                return currentDraftLenses;
            }

            return reorderLensOnCatalogSelectionChange(currentDraftLenses, expandedLensId, hasSelection);
        });
    }, [catalogDraftsByLensId, expandedLensId]);

    const handleCatalogEditingChange = useCallback((lensId: number, nextCatalog: LensCatalogData) => {
        setCatalogEditingByLensId((current) => ({
            ...current,
            [lensId]: nextCatalog,
        }));
    }, []);

    const buildLensTabResolver = useCallback(
        (catalogDrafts: Record<number, LensCatalogData>) =>
            (lensId: number): boolean | null => {
                const draft = catalogDrafts[lensId];
                if (draft) {
                    return deriveLensIsSelected(draft);
                }

                const cachedCatalog = resolveLensCatalog(
                    lensId,
                    resolvedScenarioId,
                    catalogDrafts,
                    queryClient,
                    lensId === activeLensId ? activeLensConfigResult : null,
                );
                if (cachedCatalog) {
                    return deriveLensIsSelected(cachedCatalog);
                }

                return null;
            },
        [activeLensConfigResult, activeLensId, queryClient, resolvedScenarioId],
    );

    const visibleLenses = useMemo(() => (view ? getVisibleLenses(view) : []), [view]);
    const existingLensNames = useMemo(
        () => view?.lenses.map((lens) => lens.lens_name) ?? [],
        [view],
    );
    const customLensLimit = useMemo(
        () => getCustomLensLimitFromApi(createLensConfigResult?.raw),
        [createLensConfigResult?.raw],
    );
    const customLensCount = useMemo(
        () => (view ? countPrivateLenses(view.lenses) : 0),
        [view],
    );
    const isCustomLensLimitReached =
        customLensLimit != null && customLensCount >= customLensLimit;
    const customLensLimitTooltip =
        isCustomLensLimitReached && customLensLimit != null
            ? getCustomLensLimitReachedMessage(customLensLimit)
            : null;

    const filteredDraftLenses = useMemo(() => {
        const normalizedSearch = settingsSearch.trim().toLowerCase();
        if (!normalizedSearch) {
            return settingsListLenses;
        }
        return settingsListLenses.filter((lens) => lens.lens_name.toLowerCase().includes(normalizedSearch));
    }, [settingsListLenses, settingsSearch]);

    const updateView = useCallback(
        (nextView: ScenarioViewData, source = 'update_view') => {
            setView(nextView);
            syncDirtyFromBaseline(nextView, undefined, source);
        },
        [syncDirtyFromBaseline],
    );

    const applyLocalViewUpdate = useCallback(
        (nextView: ScenarioViewData, source = 'apply_local_view_update') => {
            setView(nextView);
            syncDirtyFromBaseline(nextView, undefined, source);
        },
        [syncDirtyFromBaseline],
    );

    const handleApplyExpandedLensCatalog = useCallback(() => {
        if (!view || expandedLensId == null || resolvedScenarioId == null) {
            return;
        }

        const catalogToApply = expandedPanelCatalog;
        if (!catalogToApply) {
            return;
        }

        const validationError = validateLensCatalogSelection(catalogToApply);
        if (validationError) {
            void message.error(validationError);
            return;
        }

        const appliedCatalog = cloneLensCatalog(catalogToApply);
        const nextCatalogDrafts = {
            ...catalogDraftsByLensId,
            [expandedLensId]: appliedCatalog,
        };

        queryClient.setQueryData<LensCatalogFetchResult>(
            getLensScenarioConfigQueryKey(expandedLensId, resolvedScenarioId),
            (current) => ({
                lensId: expandedLensId,
                catalog: appliedCatalog,
                raw: current?.raw ?? null,
            }),
        );

        setCatalogDraftsByLensId(nextCatalogDrafts);
        discardCatalogEditingForLens(expandedLensId);
        clearExpandedLensPanelBaseline(expandedLensId);

        const resolveAfterApply = buildLensTabResolver(nextCatalogDrafts);
        const nextView = syncViewLensSelectionFromCatalogs(view, resolveAfterApply);
        applyLocalViewUpdate(nextView, 'apply_lens_catalog');
        setDraftLenses(sortLensesByDisplayOrder(nextView.lenses));
        setExpandedLensId(null);
    }, [
        applyLocalViewUpdate,
        buildLensTabResolver,
        clearExpandedLensPanelBaseline,
        catalogDraftsByLensId,
        discardCatalogEditingForLens,
        expandedLensId,
        expandedPanelCatalog,
        queryClient,
        resolvedScenarioId,
        view,
    ]);

    const handleResetExpandedLensCatalog = useCallback(() => {
        if (expandedLensId == null) {
            return;
        }

        const panelOpenBaseline = expandedLensPanelBaselineRef.current[expandedLensId];
        if (!panelOpenBaseline) {
            return;
        }

        setCatalogEditingByLensId((current) => ({
            ...current,
            [expandedLensId]: cloneLensCatalog(panelOpenBaseline),
        }));
    }, [expandedLensId]);

    const handleLensTabClick = (lensId: number) => {
        if (!view || lensId === activeLensId) {
            return;
        }

        if (isSettingsOpen) {
            stageInFlightCatalogEditsToDrafts();
        }

        void ensureLensConfigLoaded(lensId);

        const nextView = {
            ...view,
            lenses: view.lenses.map((lens) => ({
                ...lens,
                is_active_lens: lens.lens_id === lensId,
            })),
        };

        if (!canPersistScenarioView) {
            setView(nextView);
            return;
        }

        const baselineLens = baselineLensesRef.current.find((lens) => lens.lens_id === lensId);
        const isLensAvailable = Boolean(baselineLens?.is_selected);
        const hasOtherEdits = diagnoseViewDirtyState({
            baselineLenses: baselineLensesRef.current,
            viewLenses: view.lenses,
            draftLenses: draftLensesRef.current,
            catalogDraftsByLensId: catalogDraftsByLensIdRef.current,
            resolveBaselineCatalog,
        }).isDirty;

        if (isLensAvailable && !hasOtherEdits && resolvedScenarioId != null) {
            const orderedLenses = sortLensesByDisplayOrder(nextView.lenses);
            setView(nextView);
            setDraftLenses(orderedLenses);
            baselineLensesRef.current = orderedLenses;
            hasLocalViewEditsRef.current = false;
            isDirtyRef.current = false;
            setIsDirty(false);

            const persistSeq = ++activeLensPersistSeqRef.current;
            void setScenarioActiveLens(resolvedScenarioId, lensId).catch(() => {
                if (persistSeq !== activeLensPersistSeqRef.current) {
                    return;
                }
                void message.error('Failed to save active lens');
            });
            return;
        }

        updateView(nextView, 'lens_tab_click');
    };

    const handleSettingsDraftLensesChange = useCallback(
        (nextDraftLenses: ScenarioViewLens[]) => {
            const orderedDraftLenses = normalizeLensDisplayOrder(nextDraftLenses);
            setDraftLenses(orderedDraftLenses);
            syncDirtyFromBaseline(undefined, orderedDraftLenses, 'settings_draft_lenses_change');
        },
        [syncDirtyFromBaseline],
    );

    const handleCustomLensCreate = useCallback(
        async (data: CustomLensFormData, response?: UpsertCustomLensResponse) => {
            if (!view) return;

            const trimmedName = data.name.trim();
            if (!trimmedName) {
                return;
            }

            const resolvedLensId = response?.lens_id;
            let nextView = appendCustomLens(
                view,
                trimmedName,
                resolvedLensId,
                resolvedLensId != null ? { selectLens: true } : undefined,
            );

            setIsCustomLensModalOpen(false);
            setIsSettingsOpen(false);
            setExpandedLensId(null);

            if (resolvedLensId != null && resolvedScenarioId != null) {
                try {
                    const fetchResult = await queryClient.fetchQuery({
                        queryKey: getLensScenarioConfigQueryKey(resolvedLensId, resolvedScenarioId),
                        queryFn: () => fetchLensScenarioConfig(resolvedLensId, resolvedScenarioId),
                    });
                    const defaultCatalogDraft = materializeScenarioLensCatalogIfEmpty(fetchResult.catalog);
                    queryClient.setQueryData<LensCatalogFetchResult>(
                        getLensScenarioConfigQueryKey(resolvedLensId, resolvedScenarioId),
                        {
                            ...fetchResult,
                            catalog: defaultCatalogDraft,
                        },
                    );
                    setCatalogDraftsByLensId((prev) => ({
                        ...prev,
                        [resolvedLensId]: defaultCatalogDraft,
                    }));
                    nextView = syncViewLensSelectionFromCatalogs(nextView, (targetLensId) =>
                        targetLensId === resolvedLensId ? deriveLensIsSelected(defaultCatalogDraft) : null,
                    );
                } catch {
                    // Keep new lens tab; catalog loads when refetch succeeds.
                }
            }

            applyLocalViewUpdate(nextView, 'custom_lens_create');
            setDraftLenses(normalizeLensDisplayOrder(nextView.lenses));
            void queryClient.invalidateQueries({ queryKey: ['ppa-lens-config', null] });
            if (viewId != null && resolvedLensId != null) {
                const metadataQueryKey = getPpaViewMetadataQueryKey(viewId, resolvedScenarioId, dataSource);
                queryClient.removeQueries({ queryKey: metadataQueryKey });
                queryClient.setQueryData<ScenarioViewData>(metadataQueryKey, nextView);
                void queryClient.refetchQueries({ queryKey: metadataQueryKey });
            }
        },
        [applyLocalViewUpdate, dataSource, queryClient, resolvedScenarioId, view, viewId],
    );

    const handleCustomLensEdit = useCallback(
        async (data: CustomLensFormData, response?: UpsertCustomLensResponse) => {
            if (!view || data.id == null) {
                return;
            }

            const trimmedName = (response?.lens_name ?? data.name).trim();
            if (!trimmedName) {
                return;
            }

            const lensId = Number(data.id);
            let nextView = renameCustomLensInView(view, lensId, trimmedName);

            setCatalogDraftsByLensId((prev) => {
                if (!prev[lensId]) {
                    return prev;
                }

                const next = { ...prev };
                delete next[lensId];
                return next;
            });
            markLensConfigStaleAfterEdit(lensId);

            if (resolvedScenarioId != null) {
                try {
                    const fetchResult = await queryClient.fetchQuery({
                        queryKey: getLensScenarioConfigQueryKey(lensId, resolvedScenarioId),
                        queryFn: () => fetchLensScenarioConfig(lensId, resolvedScenarioId),
                    });
                    const defaultCatalogDraft = materializeScenarioLensCatalogIfEmpty(fetchResult.catalog);
                    queryClient.setQueryData<LensCatalogFetchResult>(
                        getLensScenarioConfigQueryKey(lensId, resolvedScenarioId),
                        {
                            ...fetchResult,
                            catalog: defaultCatalogDraft,
                        },
                    );
                    setCatalogDraftsByLensId((prev) => ({
                        ...prev,
                        [lensId]: defaultCatalogDraft,
                    }));
                    nextView = syncViewLensSelectionFromCatalogs(nextView, (targetLensId) =>
                        targetLensId === lensId ? deriveLensIsSelected(defaultCatalogDraft) : null,
                    );
                } catch {
                    // Keep renamed lens; catalog loads when refetch succeeds.
                }
            }

            applyLocalViewUpdate(nextView, 'custom_lens_edit');
            setDraftLenses(normalizeLensDisplayOrder(nextView.lenses));
            setIsEditCustomLensModalOpen(false);
            setEditingLens(null);
            setExpandedLensId(null);
            setIsSettingsOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['ppa-lens-config', null] });
            if (viewId != null) {
                const metadataQueryKey = getPpaViewMetadataQueryKey(viewId, resolvedScenarioId, dataSource);
                queryClient.removeQueries({ queryKey: metadataQueryKey });
                queryClient.setQueryData<ScenarioViewData>(metadataQueryKey, nextView);
                void queryClient.refetchQueries({ queryKey: metadataQueryKey });
            }
        },
        [applyLocalViewUpdate, dataSource, markLensConfigStaleAfterEdit, queryClient, resolvedScenarioId, view, viewId],
    );

    const handleCustomLensDelete = useCallback(
        (
            deletedLensId?: string | number,
            options?: { fallbackLensId?: number; fallbackLensName?: string },
        ) => {
            if (!view || deletedLensId == null || deletedLensId === '') {
                return;
            }

            const lensId = Number(deletedLensId);
            const settingsOrderedLenses = reorderLensesSelectedFirst(
                mergeViewLensesIntoDraft(view, draftLenses).map((lens) => ({
                    ...lens,
                    is_selected: resolveSettingsLensIsSelected(lens.lens_id, lens.is_selected),
                })),
            );
            const selectedLensesBeforeDelete = settingsOrderedLenses.filter((lens) => lens.is_selected);
            const wasActiveDeleted = activeLensId === lensId;
            const wasExpandedDeleted = expandedLensId === lensId;
            const activeSuccessorId = wasActiveDeleted
                ? (
                    options?.fallbackLensId
                    ?? resolveActiveLensIdAfterDelete(selectedLensesBeforeDelete, lensId, view.lenses)
                )
                : null;
            const expandedSuccessorId = wasExpandedDeleted
                ? resolveLensIdAfterDelete(settingsOrderedLenses, lensId)
                    ?? resolveActiveLensIdAfterDelete(settingsOrderedLenses, lensId, view.lenses)
                : null;

            const nextView = removeCustomLensFromView(view, lensId, {
                activeSuccessorLensId: activeSuccessorId,
            });

            hasLocalViewEditsRef.current = false;
            isDirtyRef.current = false;
            setIsDirty(false);
            hydratedViewContextRef.current = null;

            updateView(nextView, 'custom_lens_delete');
            setDraftLenses((currentDraftLenses) => {
                const filteredLenses = normalizeLensDisplayOrder(
                    currentDraftLenses.filter((lens) => lens.lens_id !== lensId),
                );
                if (activeSuccessorId == null) {
                    return filteredLenses;
                }

                return filteredLenses.map((lens) => ({
                    ...lens,
                    is_active_lens: lens.lens_id === activeSuccessorId,
                }));
            });

            if (wasExpandedDeleted) {
                if (expandedSuccessorId != null) {
                    discardCatalogEditingForLens(lensId);
                    clearExpandedLensPanelBaseline(lensId);
                    clearExpandedLensPanelBaseline(expandedSuccessorId);
                    setExpandedLensId(expandedSuccessorId);
                    void loadExpandedLensConfig(expandedSuccessorId);
                } else {
                    setExpandedLensId(null);
                }
            }

            setCatalogDraftsByLensId((currentDrafts) => {
                if (!currentDrafts[lensId]) {
                    return currentDrafts;
                }

                const nextDrafts = { ...currentDrafts };
                delete nextDrafts[lensId];
                return nextDrafts;
            });
            discardCatalogEditingForLens(lensId);
            // Drop the deleted lens's caches instead of invalidating them. Invalidate would
            // immediately refetch any still-active observer (e.g. the edit modal's
            // useFetchLensConfig, which is mounted until `open` flips to false on the next
            // render), firing a GET for a lens that no longer exists → 404.
            queryClient.removeQueries({ queryKey: ['ppa-lens-config', lensId] });
            void queryClient.invalidateQueries({ queryKey: ['ppa-lens-config', null] });
            if (resolvedScenarioId != null) {
                queryClient.removeQueries({
                    queryKey: getLensScenarioConfigQueryKey(lensId, resolvedScenarioId),
                });
            }

            invalidateLensDeleteWorkspaceCache(queryClient, {
                scenarioId: resolvedScenarioId,
                viewId,
                userId: getAuthUserMail() || null,
            });

            if (canPersistScenarioView && wasActiveDeleted && activeSuccessorId != null && resolvedScenarioId != null) {
                void (async () => {
                    try {
                        await setScenarioActiveLens(resolvedScenarioId, activeSuccessorId);
                    } catch {
                        void message.error('Failed to save active lens');
                        return;
                    }

                    if (viewId != null) {
                        for (const nextDataSource of ['view', 'scenario'] as const) {
                            void queryClient.refetchQueries({
                                queryKey: getPpaViewMetadataQueryKey(
                                    viewId,
                                    resolvedScenarioId,
                                    nextDataSource,
                                ),
                            });
                        }
                    }
                })();
            } else if (viewId != null) {
                for (const nextDataSource of ['view', 'scenario'] as const) {
                    void queryClient.refetchQueries({
                        queryKey: getPpaViewMetadataQueryKey(
                            viewId,
                            resolvedScenarioId,
                            nextDataSource,
                        ),
                    });
                }
            }
            setIsEditCustomLensModalOpen(false);
            setEditingLens(null);
        },
        [
            activeLensId,
            canPersistScenarioView,
            clearExpandedLensPanelBaseline,
            discardCatalogEditingForLens,
            draftLenses,
            expandedLensId,
            loadExpandedLensConfig,
            queryClient,
            resolveSettingsLensIsSelected,
            resolvedScenarioId,
            updateView,
            view,
            viewId,
        ],
    );

    const isSearchActive = settingsSearch.trim().length > 0;
    const isExpandedLensConfigLoading =
        isRefreshingExpandedLensConfig ||
        (expandedLensId != null && !expandedPanelCatalog && isExpandedLensQueryInFlight);
    const expandedLens = useMemo(
        () => draftLenses.find((lens) => lens.lens_id === expandedLensId) ?? null,
        [draftLenses, expandedLensId],
    );

    const canEditExpandedLens = useMemo(() => {
        if (!getLensCanEditFromApi(expandedLensQueryResult?.raw)) {
            return false;
        }

        if (canPersistScenarioView) {
            return true;
        }

        return Boolean(expandedLens?.is_private);
    }, [canPersistScenarioView, expandedLens?.is_private, expandedLensQueryResult?.raw]);

    const resetSettingsPanelUI = useCallback(() => {
        const resetExpandedLensId = expandedLensId;
        setSettingsSearch('');
        setExpandedLensId(null);
        setCatalogEditingByLensId({});
        expandedLensPanelBaselineRef.current = {};

        if (canPersistScenarioView && viewRef.current) {
            setDraftLenses(
                sortLensesByDisplayOrder(viewRef.current.lenses.map((lens) => ({ ...lens }))),
            );
        }

        if (resetExpandedLensId != null && resolvedScenarioId != null) {
            void queryClient.invalidateQueries({
                queryKey: getLensScenarioConfigQueryKey(resetExpandedLensId, resolvedScenarioId),
            });
        }
    }, [canPersistScenarioView, expandedLensId, queryClient, resolvedScenarioId]);

    const stageInFlightCatalogEditsToDrafts = useCallback(() => {
        const inFlightEdits = catalogEditingByLensIdRef.current;
        const inFlightLensIds = Object.keys(inFlightEdits);
        if (inFlightLensIds.length === 0) {
            return catalogDraftsByLensIdRef.current;
        }

        const nextCatalogDrafts = {
            ...catalogDraftsByLensIdRef.current,
        };

        for (const lensIdKey of inFlightLensIds) {
            const lensId = Number(lensIdKey);
            const stagedCatalog = cloneLensCatalog(inFlightEdits[lensId]);
            nextCatalogDrafts[lensId] = stagedCatalog;

            if (resolvedScenarioId != null) {
                queryClient.setQueryData<LensCatalogFetchResult>(
                    getLensScenarioConfigQueryKey(lensId, resolvedScenarioId),
                    (current) => ({
                        lensId,
                        catalog: stagedCatalog,
                        raw: current?.raw ?? null,
                    }),
                );
            }
        }

        catalogDraftsByLensIdRef.current = nextCatalogDrafts;
        catalogEditingByLensIdRef.current = {};
        setCatalogDraftsByLensId(nextCatalogDrafts);
        setCatalogEditingByLensId({});
        return nextCatalogDrafts;
    }, [queryClient, resolvedScenarioId]);

    const commitSettingsDraftOnClose = useCallback(() => {
        if (!viewRef.current) {
            return;
        }

        const mergedCatalogDrafts = stageInFlightCatalogEditsToDrafts();
        const currentView = viewRef.current;
        const mergedDraft = mergeViewLensesIntoDraft(currentView, draftLensesRef.current);

        if (
            !hasSettingsPanelDraftChanges(currentView.lenses, mergedDraft, mergedCatalogDrafts)
        ) {
            return;
        }

        let nextView = applyLensDraftToView(currentView, mergedDraft);
        if (Object.keys(mergedCatalogDrafts).length > 0) {
            nextView = syncViewLensSelectionFromCatalogs(nextView, buildLensTabResolver(mergedCatalogDrafts));
        }
        viewRef.current = nextView;
        applyLocalViewUpdate(nextView, 'commit_settings_draft_on_close');
    }, [applyLocalViewUpdate, buildLensTabResolver, stageInFlightCatalogEditsToDrafts]);

    const resetSettingsDraft = useCallback(() => {
        resetSettingsPanelUI();
        if (canPersistScenarioView) {
            setCatalogDraftsByLensId({});
        }
    }, [canPersistScenarioView, resetSettingsPanelUI]);

    const closeSettingsPanelForLensEditModal = useCallback(() => {
        skipSettingsCloseSideEffectsRef.current = true;
        setSettingsSearch('');
        setExpandedLensId(null);
        setCatalogEditingByLensId({});
        expandedLensPanelBaselineRef.current = {};
        setIsSettingsOpen(false);
    }, []);

    const handleSystemLensEditComplete = useCallback(
        async (editedLensId: number) => {
            setCatalogDraftsByLensId((prev) => {
                const next = { ...prev };
                delete next[editedLensId];
                return next;
            });
            markLensConfigStaleAfterEdit(editedLensId);
            await refetchLensConfigAfterEdit(editedLensId);

            const currentView = viewRef.current;
            if (!currentView) {
                return;
            }

            const syncedView = syncViewLensSelectionFromCatalogs(currentView, resolveLensTabIsSelected);
            const resolveFreshCatalogForLens = (lensId: number) =>
                resolveLensCatalog(lensId, resolvedScenarioId, {}, queryClient);

            applyLocalViewUpdate(
                {
                    ...currentView,
                    column_visibility: buildColumnVisibilityFromLenses({
                        lenses: syncedView.lenses,
                        resolveCatalogForLens: resolveFreshCatalogForLens,
                        fallbackColumnVisibility: currentView.column_visibility,
                    }),
                },
                'sync_column_visibility_after_system_lens_edit',
            );
        },
        [
            applyLocalViewUpdate,
            markLensConfigStaleAfterEdit,
            queryClient,
            refetchLensConfigAfterEdit,
            resolveLensTabIsSelected,
            resolvedScenarioId,
        ],
    );

    const handleEditCustomLens = useCallback(() => {
        if (!expandedLens || expandedLensId == null) {
            return;
        }

        if (!canPersistScenarioView && !expandedLens.is_private) {
            return;
        }

        const configResult = getCachedLensConfigResult(expandedLensId, resolvedScenarioId, queryClient);
        if (!configResult) {
            return;
        }

        setEditingLens(expandedLens);
        closeSettingsPanelForLensEditModal();

        if (getLensCanModifyCatalogFromApi(configResult.raw)) {
            setIsEditCustomLensModalOpen(true);
            return;
        }

        setIsEditSystemLensModalOpen(true);
    }, [canPersistScenarioView, closeSettingsPanelForLensEditModal, expandedLens, expandedLensId, queryClient, resolvedScenarioId]);

    const handleOpenCustomLensModal = useCallback(() => {
        if (isCustomLensLimitReached) {
            return;
        }

        resetSettingsDraft();
        setIsSettingsOpen(false);
        setIsCustomLensModalOpen(true);
    }, [isCustomLensLimitReached, resetSettingsDraft]);

    const handleSettingsOpenChange = useCallback(
        (open: boolean) => {
            if (!open) {
                if (skipSettingsCloseSideEffectsRef.current) {
                    skipSettingsCloseSideEffectsRef.current = false;
                } else {
                    commitSettingsDraftOnClose();
                    resetSettingsPanelUI();
                }
            }
            setIsSettingsOpen(open);
        },
        [commitSettingsDraftOnClose, resetSettingsPanelUI],
    );

    const customLensRow = useMemo(
        () => (
            <StyledCustomLensRow
                type="button"
                isDisabled={isCustomLensLimitReached}
                disabled={isCustomLensLimitReached}
                onClick={handleOpenCustomLensModal}
            >
                <span className="plus-icon">
                    <PlusOutlined />
                </span>
                <span className="custom-lens-label">Custom Lens</span>
            </StyledCustomLensRow>
        ),
        [handleOpenCustomLensModal, isCustomLensLimitReached],
    );

    const settingsPanel = useMemo(
        () => (
            <StyledLensSettingsDropdownWrapper onClick={(event) => event.stopPropagation()}>
                <StyledLensSettingsPanel>
                    <StyledLensSettingsSearch>
                        <Input
                            value={settingsSearch}
                            onChange={(event) => setSettingsSearch(event.target.value)}
                            placeholder="Search"
                            suffix={<SearchOutlined className="text-black/45" />}
                            className="h-8 !rounded-md"
                        />
                    </StyledLensSettingsSearch>
                    <LensSettingsSortableList
                        lenses={settingsListLenses}
                        displayLenses={isSearchActive ? filteredDraftLenses : undefined}
                        onChange={handleSettingsDraftLensesChange}
                        sortable={!isSearchActive}
                        showDragHandle={!isSearchActive}
                        expandedLensId={expandedLensId}
                        onExpandLens={handleExpandLens}
                    />
                    {customLensLimitTooltip ? (
                        <Tooltip {...CUSTOM_LENS_TOOLTIP_PROPS} title={customLensLimitTooltip}>
                            <span className="block">{customLensRow}</span>
                        </Tooltip>
                    ) : (
                        customLensRow
                    )}
                </StyledLensSettingsPanel>
                {expandedLensId != null ? (
                    <StyledLensConfigurationDropdown>
                        {isExpandedLensConfigLoading ? (
                            <StyledLensConfigurationLoading>
                                <Spin size="small" />
                            </StyledLensConfigurationLoading>
                        ) : expandedPanelCatalog ? (
                            <LensConfigurationPanel
                                key={`${expandedLensId}-${catalogConfigRevision}`}
                                catalog={expandedPanelCatalog}
                                baselineCatalog={expandedCommittedCatalog}
                                readOnly={false}
                                onEdit={canEditExpandedLens ? handleEditCustomLens : undefined}
                                onChange={(nextCatalog) => handleCatalogEditingChange(expandedLensId, nextCatalog)}
                                onApply={handleApplyExpandedLensCatalog}
                                onReset={handleResetExpandedLensCatalog}
                            />
                        ) : null}
                    </StyledLensConfigurationDropdown>
                ) : null}
            </StyledLensSettingsDropdownWrapper>
        ),
        [
            canEditExpandedLens,
            catalogConfigRevision,
            customLensLimitTooltip,
            customLensRow,
            expandedLensId,
            expandedCommittedCatalog,
            expandedPanelCatalog,
            filteredDraftLenses,
            handleApplyExpandedLensCatalog,
            handleCatalogEditingChange,
            handleEditCustomLens,
            handleExpandLens,
            handleResetExpandedLensCatalog,
            handleSettingsDraftLensesChange,
            isExpandedLensConfigLoading,
            isSearchActive,
            settingsListLenses,
            settingsSearch,
        ],
    );

    const renderSettingsDropdown = useCallback(() => settingsPanel, [settingsPanel]);

    if ((!isViewDetached && viewId == null) || isViewDataLoading || !view) {
        return (
            <StyledLensTabsRow>
                <Spin size="small" />
            </StyledLensTabsRow>
        );
    }

    return (
        <>
            <StyledLensSettingsDropdownGlobalStyles />
            <StyledLensTabsRow>
                <Dropdown
                    key={`lens-settings-${view.lenses.length}-${draftLenses.length}`}
                    trigger={['click']}
                    open={isSettingsOpen}
                    onOpenChange={handleSettingsOpenChange}
                    dropdownRender={renderSettingsDropdown}
                    placement="bottomLeft"
                    destroyPopupOnHide
                    overlayClassName={LENS_SETTINGS_DROPDOWN_CLASS}
                    getPopupContainer={(triggerNode) => triggerNode.parentElement ?? document.body}
                >
                    <StyledSettingsButton
                        type="text"
                        icon={<SettingOutlined />}
                        aria-label="Lens settings"
                        aria-expanded={isSettingsOpen}
                    />
                </Dropdown>
                <StyledLensTabsTrack>
                    <StyledLensTabsInner>
                        {visibleLenses.map((lens) => (
                            <StyledLensTab
                                key={lens.lens_id}
                                type="button"
                                isActive={lens.lens_id === activeLens?.lens_id}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => handleLensTabClick(lens.lens_id)}
                            >
                                {lens.lens_name}
                            </StyledLensTab>
                        ))}
                    </StyledLensTabsInner>
                </StyledLensTabsTrack>
            </StyledLensTabsRow>
            <StyledLensContent>
                <ScenarioLensTableShell
                    catalog={resolvedActiveCatalog}
                    isLoading={isActiveLensCatalogLoading}
                    lensName={activeLens?.lens_name}
                />
            </StyledLensContent>
            <CreateCustomLensModal
                open={isCustomLensModalOpen}
                existingLensNames={existingLensNames}
                scenarioId={resolvedScenarioId}
                baseViewId={viewId}
                onCancel={() => setIsCustomLensModalOpen(false)}
                onOk={handleCustomLensCreate}
            />
            <EditSystemLensModal
                open={isEditSystemLensModalOpen}
                lensId={editingLens?.lens_id ?? null}
                originViewId={viewId}
                originScenarioId={resolvedScenarioId}
                onCancel={() => {
                    setIsEditSystemLensModalOpen(false);
                    setEditingLens(null);
                }}
                onOk={() => {
                    const editedLensId = editingLens?.lens_id ?? null;
                    setIsEditSystemLensModalOpen(false);
                    setEditingLens(null);
                    setExpandedLensId(null);
                    setIsSettingsOpen(false);
                    if (editedLensId != null) {
                        void handleSystemLensEditComplete(editedLensId);
                    }
                }}
            />
            <CreateCustomLensModal
                open={isEditCustomLensModalOpen}
                isEdit
                lensData={
                    editingLens
                        ? {
                              id: editingLens.lens_id,
                              name: editingLens.lens_name,
                              includeAiSuggestions: false,
                              dimensionSelections: {},
                              kpiSelections: {},
                              selectedDimensionOrder: [],
                              selectedKpiOrder: [],
                          }
                        : undefined
                }
                existingLensNames={existingLensNames}
                scenarioId={resolvedScenarioId}
                baseViewId={viewId}
                onCancel={() => {
                    setIsEditCustomLensModalOpen(false);
                    setEditingLens(null);
                }}
                onOk={handleCustomLensEdit}
                onDelete={handleCustomLensDelete}
            />
            <SimulatorContextBanner messages={contextMessages} onDismiss={dismissContextMessage} />
        </>
    );
};

export default ScenarioLensSection;
